import React, { useState } from 'react';
import { TicketsService } from '../services/ticketsService';
import {
  TICKET_CATEGORIES,
  TICKET_CATEGORY_LABELS,
  type TicketCategory,
} from '../types/ticket';

const inputClass =
  'w-full px-3 py-2 bg-[#334155] border border-[#475569] rounded-lg text-white placeholder-[#94a3b8] focus:outline-none focus:border-[#1f9e3f] focus:ring-2 focus:ring-[rgba(64,206,66,0.25)] text-sm';
const labelClass = 'block text-xs font-medium text-[#cbd5e1] mb-1.5';

interface NewTicketFormProps {
  onCreated?: () => void;
}

const NewTicketForm: React.FC<NewTicketFormProps> = ({ onCreated }) => {
  const [category, setCategory] = useState<TicketCategory>('suggestion');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setOk(false);
    try {
      await TicketsService.create({ category, subject, description });
      setSubject('');
      setDescription('');
      setCategory('suggestion');
      setOk(true);
      onCreated?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="max-w-2xl bg-[#1e293b] rounded-xl border border-[#334155] p-4 sm:p-6 space-y-4"
    >
      <div>
        <h3 className="text-white text-lg font-semibold mb-1">
          Abrir una petición
        </h3>
        <p className="text-[#94a3b8] text-sm">
          Cuéntanos tu sugerencia, incidencia o duda. El equipo la revisará.
        </p>
      </div>

      {error && (
        <div
          className="p-3 rounded-lg text-sm"
          style={{
            background: '#ef444415',
            border: '1px solid #ef444455',
            color: '#fca5a5',
          }}
        >
          {error}
        </div>
      )}

      {ok && (
        <div className="p-3 rounded-lg text-sm bg-[#1f9e3f22] border border-[#1f9e3f55] text-[#6ee06f]">
          ¡Petición enviada! Puedes seguirla en «Mis tickets».
        </div>
      )}

      <div>
        <label className={labelClass} htmlFor="ticket-category">
          Tipo
        </label>
        <select
          id="ticket-category"
          value={category}
          onChange={(e) => setCategory(e.target.value as TicketCategory)}
          className={`${inputClass} cursor-pointer`}
        >
          {TICKET_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {TICKET_CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={labelClass} htmlFor="ticket-subject">
          Asunto
        </label>
        <input
          id="ticket-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          minLength={3}
          maxLength={200}
          placeholder="Resumen breve"
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass} htmlFor="ticket-description">
          Descripción
        </label>
        <textarea
          id="ticket-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          minLength={5}
          maxLength={5000}
          rows={5}
          placeholder="Explica con detalle tu petición"
          className={`${inputClass} resize-none`}
        />
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#1f9e3f] hover:opacity-90 disabled:opacity-60"
        >
          {busy ? 'Enviando…' : 'Enviar petición'}
        </button>
      </div>
    </form>
  );
};

export default NewTicketForm;
